import { useState } from 'react';
import api from '@/lib/api';

export default function EnrichContactBadge({
  candidateId,
  enrichment: initialEnrichment,
  onEnriched,
}) {
  const [enrichment, setEnrichment] = useState(initialEnrichment || null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const isEnriched = enrichment?.enriched === true;
  const bestEmail  = enrichment?.personal_emails?.[0] || null;
  const bestPhone  = enrichment?.personal_phones?.[0] || null;

  const handleEnrich = async (e) => {
    e.stopPropagation();
    setLoading(true); setError('');
    try {
      const r = await api.post(`/enrichment/candidate/${candidateId}`);
      const data = r.data.enrichment;
      setEnrichment(data);
      if (onEnriched) onEnriched(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Enrichment failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isEnriched) {
    return (
      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <button
          onClick={handleEnrich}
          disabled={loading}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs
                     bg-blue-50 text-blue-600 border border-blue-200
                     rounded-full hover:bg-blue-100 disabled:opacity-50
                     whitespace-nowrap"
          data-testid={`enrich-btn-${candidateId}`}
        >
          {loading
            ? <><span className="animate-spin inline-block">⟳</span> Enriching...</>
            : <>🔍 Enrich</>
          }
        </button>
        {error && (
          <span className="text-xs text-red-500 ml-1">{error}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5"
         onClick={e => e.stopPropagation()}>

      {/* Enriched status badge */}
      <span className="inline-flex items-center px-2 py-0.5 text-xs
                       bg-green-50 text-green-700 border border-green-200
                       rounded-full font-medium whitespace-nowrap">
        ✓ Enriched
      </span>

      {/* Personal email chip — blue ✓ only when verified=true */}
      {bestEmail && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs
                     bg-white text-gray-700 border border-gray-200
                     rounded-full max-w-[200px] truncate"
          title={bestEmail.email}
          data-testid={`email-chip-${candidateId}`}
        >
          {bestEmail.verified && (
            <span className="text-blue-500 font-bold shrink-0"
                  title="Verified — mail_status: Valid">✓</span>
          )}
          {bestEmail.email}
        </span>
      )}

      {/* Personal phone chip — grayed when is_valid=false */}
      {bestPhone && (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5
                      text-xs border rounded-full whitespace-nowrap
                      ${bestPhone.is_valid
                        ? 'bg-white text-gray-700 border-gray-200'
                        : 'bg-gray-50 text-gray-400 border-gray-100'}`}
          title={bestPhone.is_valid ? 'Valid number' : 'Unverified number'}
          data-testid={`phone-chip-${candidateId}`}
        >
          📱 {bestPhone.phone}
        </span>
      )}

      {/* Enriched but zero contact data */}
      {!bestEmail && !bestPhone && (
        <span className="text-xs text-amber-500 whitespace-nowrap"
              data-testid={`no-contact-${candidateId}`}>
          ⚠ No personal contact
        </span>
      )}
    </div>
  );
}
